import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, History, Activity, Zap, BarChart, TestTube, GitCompare, Target } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { RealTimeBotLinkingCard } from './RealTimeBotLinkingCard';
import { RealTimePredictionsMonitor } from './RealTimePredictionsMonitor';
import { RealTimeTradesHistory } from './RealTimeTradesHistory';
import { ReAnalysisEngine } from './ReAnalysisEngine';
import { SignalProcessingHistory } from './SignalProcessingHistory';
import { ReAnalysisManager } from '../re-analysis';
import { PatternValidationTester } from './PatternValidationTester';
import { CrossPatternComparisonTab } from '../re-analysis/CrossPatternComparisonTab';
import { ManualHonestSimulationPrediction } from './ManualHonestSimulationPrediction';

interface RealTimePredictionsTabProps {
  refreshTrigger?: number;
}

export const RealTimePredictionsTab: React.FC<RealTimePredictionsTabProps> = ({ refreshTrigger }) => {
  const isMobile = useIsMobile();
  const [activeSubTab, setActiveSubTab] = useState('linking');
  const [selectedBotId, setSelectedBotId] = useState<string>('');

  const handleLinkingChange = () => {
    // Refresh predictions monitor when linking changes
    if (activeSubTab === 'monitor') {
      window.location.reload();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>Real-time Predictions</h3>
          <p className={`text-muted-foreground ${isMobile ? 'text-sm' : ''}`}>
            Link bots to CSV files and monitor live trading predictions
          </p>
        </div>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-6">
        <TabsList className={isMobile ? 
          "flex flex-wrap gap-1 p-1 w-full h-auto" : 
          "grid w-full grid-cols-8"
        }>
          <TabsTrigger value="linking" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <Link className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Linking' : 'Bot-CSV Linking'}</span>
          </TabsTrigger>
          <TabsTrigger value="monitor" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <Activity className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Monitor' : 'Live Monitor'}</span>
          </TabsTrigger>
          <TabsTrigger value="processing" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <Zap className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Processing' : 'Signal Processing'}</span>
          </TabsTrigger>
          <TabsTrigger value="history" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <History className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'History' : 'Trades History'}</span>
          </TabsTrigger>
          <TabsTrigger value="reanalysis" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <BarChart className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Re-Analysis' : 'Re-Analysis'}</span>
          </TabsTrigger>
          <TabsTrigger value="validation" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <TestTube className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Validation' : 'Pattern Validation'}</span>
          </TabsTrigger>
          <TabsTrigger value="comparison" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <GitCompare className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Compare' : 'Pattern Compare'}</span>
          </TabsTrigger>
          <TabsTrigger value="manual-honest" className={`${isMobile ? 'text-xs px-2 py-2 h-auto flex flex-col items-center justify-center gap-1 flex-1 min-w-[calc(50%-0.25rem)]' : ''}`}>
            <Target className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4 mr-2'}`} />
            <span>{isMobile ? 'Manual' : 'Manual Honest'}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="linking" className="space-y-6">
          <RealTimeBotLinkingCard 
            onLinkingChange={handleLinkingChange}
            onBotSelect={setSelectedBotId}
            refreshTrigger={refreshTrigger}
          />
          <ReAnalysisEngine selectedBotId={selectedBotId} />
        </TabsContent>

        <TabsContent value="monitor" className="space-y-6">
          <RealTimePredictionsMonitor />
        </TabsContent>

        <TabsContent value="processing" className="space-y-6">
          <SignalProcessingHistory />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <RealTimeTradesHistory 
            selectedBotId={selectedBotId}
            onBotSelect={setSelectedBotId}
          />
        </TabsContent>

        <TabsContent value="reanalysis" className="space-y-6">
          <ReAnalysisManager />
        </TabsContent>

        <TabsContent value="validation" className="space-y-6">
          <PatternValidationTester />
        </TabsContent>

        <TabsContent value="comparison" className="space-y-6">
          <CrossPatternComparisonTab 
            refreshTrigger={refreshTrigger}
          />
        </TabsContent>

        <TabsContent value="manual-honest" className="space-y-6">
          <ManualHonestSimulationPrediction />
        </TabsContent>
      </Tabs>
    </div>
  );
};